import {
    Navigate,
    useParams,
    useNavigate,
    useOutletContext,
} from 'react-router';
import { useTranslation } from 'react-i18next';
import { mdiCog, mdiPlus } from '@mdi/js';
import { useEffect } from 'react';
import { useLazyQuery, useQuery } from '@apollo/client/react';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Theme } from '@mui/material/styles';

import { Icon } from 'common/components/Icon';
import { useRouteMatch } from 'route/hooks/useRouteMatch';
import { BasePage } from 'hydra/pages/BasePage';
import { Loader } from 'common/components/Loader';
import { CollectionDocument, CurrentUserDocument, IRole } from 'graphql/types';
import { ApolloError } from 'common/components/ApolloError';
import { PageIntroHeader } from 'common/components/PageIntroHeader';
import { LibraryItemsList } from 'library/components/LibraryItemsList';
import { SearchBar } from 'common/components/SearchBar';
import { Text } from 'common/components/Text';
import { Link } from 'common/components/Link';
import { ActionButton } from 'common/components/ActionButton';
import { Button } from 'common/components/Button';
import { List } from 'common/components/List';
import { ListItem } from 'common/components/ListItem';
import { Chip, ChipGroup } from 'common/components/Chip';
import { Alert } from 'common/components/Alert';
import {
    LibraryCollectionDrawer,
    LibraryCollectionCreateDrawer,
} from 'library/components/LibraryCollectionDrawer';
import { LibraryArticleCreateDrawer } from 'library/components/LibraryArticleDrawer';
import { getUrl } from 'route/utils/getUrl';
import {
    goToCollectionDetailPage,
    goToParentCollection,
    goToArticleContentEditPage,
    goToRootCollectionDetailPage,
} from 'library/utils/goTo';
import { useFrontendPermissions } from 'user/hooks';
import { PageTitle } from 'common/components/PageTitle';
import { isUUID } from 'common/utils/isUUID';
import { formatColor } from 'common/utils/color';
import { LibraryContextActionBar } from 'library/components/LibraryContextActionBar';

import { LibraryEmptyPage } from './LibraryEmptyPage';
import { FourOFourPage } from './FourOFourPage';

export const LibraryCollectionPage = () => {
    const [translate] = useTranslation();
    const { id } = useParams();
    const navigate = useNavigate();
    const smallScreen = useMediaQuery((theme: Theme) =>
        theme.breakpoints.only('xs')
    );
    const { searchQuery, onSearch, showLibrary } = useOutletContext<{
        searchQuery?: string;
        showLibrary?: boolean;
        onSearch(value: string): void;
    }>();
    const { data: currentUserData } = useQuery(CurrentUserDocument);

    const currentUser = currentUserData?.currentUser;

    const isManager = currentUser?.roles?.includes(IRole.Manager);
    const isAuthor = currentUser?.roles?.includes(IRole.Author);

    const { canCreate: canCreateCollection, canUpdate: canUpdateCollection } =
        useFrontendPermissions('libraryCollection');
    const { canCreate: canCreateArticle } =
        useFrontendPermissions('libraryArticle');

    const createCollectionDrawerOpen = !!useRouteMatch(
        'LIBRARY_COLLECTION_CREATE'
    );
    const createArticleDrawerOpen = !!useRouteMatch(
        'LIBRARY_COLLECTION_CREATE_ARTICLE'
    );
    const collectionDrawerOpen = !!useRouteMatch('LIBRARY_COLLECTION_EDIT');

    const [
        fetchCollection,
        {
            loading: loadingCollection,
            error,
            data,
            called,
            refetch: refetchCollection,
        },
    ] = useLazyQuery(CollectionDocument);

    useEffect(() => {
        const collection = data?.libraryCollection;

        if (!collection || !id || collection.root || isUUID(id)) return;

        // When a normal id is used redirect to url with UUID
        navigate(
            getUrl(
                collectionDrawerOpen
                    ? 'LIBRARY_COLLECTION_EDIT'
                    : 'LIBRARY_COLLECTION',
                {
                    id: collection.id,
                }
            )
        );
    }, [data, collectionDrawerOpen, id, navigate]);

    const loading = loadingCollection;

    useEffect(() => {
        const collectionId = data?.libraryCollection?.id || {};

        if (loading || collectionId === id || error) return;

        const collectionQueryVariables = isUUID(id)
            ? { id }
            : { collectionId: id };

        fetchCollection({ variables: collectionQueryVariables });
    }, [data, fetchCollection, id, loading, error]);

    // Early return when loading or error
    if (loading || !called || error) {
        return (
            <BasePage>
                {error && <ApolloError error={error} />}
                {(loading || !called) && <Loader />}
            </BasePage>
        );
    }

    const collection = data?.libraryCollection;

    // When the response doesn't contain an collection we redirect to the root of the library.
    if (called && !loading && !collection) {
        if (!id) {
            if (canCreateCollection) {
                // To get the new root collection on create we call refetch
                return (
                    <LibraryEmptyPage
                        onCreate={() =>
                            refetchCollection && refetchCollection()
                        }
                    />
                );
            }

            // We don't have a root collection and the user cannot create it. We return a 404 page
            return <FourOFourPage />;
        }

        return <Navigate to={getUrl('LIBRARY')} />;
    }

    const {
        id: collectionId,
        parent,
        title,
        description,
        root,
        active,
        titleColor,
        headerImage,
        collections,
        articles,
    } = collection || {};

    let headerColor = formatColor(titleColor);

    if (!headerColor) {
        headerColor = headerImage ? 'common.white' : undefined;
    }

    const libraryTitle = translate('library');

    const collectionSettingsButton = canUpdateCollection && (
        <Button
            aria-label={translate('collectionSettings')}
            component={Link}
            params={{ id: collectionId }}
            size="small"
            startIcon={<Icon path={mdiCog} size="1.9rem" />}
            sx={
                smallScreen
                    ? { minWidth: 0, '.MuiButton-icon': { mr: -1 } }
                    : undefined
            }
            to="LIBRARY_COLLECTION_EDIT"
            variant="outlined"
        >
            {!smallScreen && translate('collectionSettings')}
        </Button>
    );

    const addButtons = (canCreateCollection || canCreateArticle) && (
        <Stack direction="row" spacing={1}>
            {canCreateCollection && (
                <ActionButton
                    aria-label={translate('createCollection')}
                    outlined={!headerImage}
                    variant="extended"
                    onClick={() => {
                        navigate(
                            getUrl('LIBRARY_COLLECTION_CREATE', {
                                id: collectionId,
                            })
                        );
                    }}
                >
                    <Box>
                        <Icon path={mdiPlus} size="2.4rem" />
                    </Box>
                    {translate('collection')}
                </ActionButton>
            )}
            {canCreateArticle && (
                <ActionButton
                    aria-label={translate('createArticle')}
                    outlined={!headerImage}
                    variant="extended"
                    onClick={() => {
                        navigate(
                            getUrl('LIBRARY_COLLECTION_CREATE_ARTICLE', {
                                id: collectionId,
                            })
                        );
                    }}
                >
                    <Box>
                        <Icon path={mdiPlus} size="2.4rem" />
                    </Box>
                    {translate('article')}
                </ActionButton>
            )}
        </Stack>
    );

    const titleRow = (
        <Grid
            container
            spacing={2}
            sx={{
                alignItems: 'flex-end',
                justifyContent: 'space-between',
                width: '100%',
            }}
        >
            <Grid>
                <Typography sx={{ color: headerColor }} variant="h1">
                    {title}
                </Typography>
            </Grid>
            {addButtons && <Grid sx={{ flexShrink: 0 }}>{addButtons}</Grid>}
        </Grid>
    );

    let header;

    if (headerImage) {
        // Default header color is white when image is set

        header = (
            <PageIntroHeader image={headerImage}>
                {!active && (
                    <Box sx={{ mb: 1 }}>
                        <ChipGroup>
                            <Chip
                                bgColor="warning"
                                label={translate('hidden')}
                            />
                        </ChipGroup>
                    </Box>
                )}
                {titleRow}
            </PageIntroHeader>
        );
    }

    // The root collection has no parent to return to, and deleting it leaves
    // nothing to show: its detail page renders the empty library response.
    const backToCurrentCollection = () => {
        if (!parent || !id) {
            goToRootCollectionDetailPage(navigate);

            return;
        }

        goToCollectionDetailPage(navigate, id);
    };

    const backToParentCollection = () => {
        if (!parent) {
            goToRootCollectionDetailPage(navigate);

            return;
        }

        goToParentCollection(navigate, parent);
    };

    const contextActionBar = (
        <LibraryContextActionBar
            actions={collectionSettingsButton}
            libraryTitle={root ? undefined : libraryTitle}
            parent={parent}
        />
    );

    const pageTitle = root
        ? libraryTitle
        : title
          ? `${libraryTitle} - ${title}`
          : undefined;

    const showNotPublishedAlert =
        root && !showLibrary && (isManager || isAuthor);

    return (
        <BasePage contextActionBar={contextActionBar} header={header}>
            {collection && <PageTitle>{pageTitle || ''}</PageTitle>}
            <Box sx={{ py: 4 }}>
                {!header && (
                    <Box sx={{ mb: 2 }}>
                        {!root && !active && (
                            <Box sx={{ mb: 1 }}>
                                <Chip
                                    bgColor="warning"
                                    label={translate('hidden')}
                                />
                            </Box>
                        )}
                        {titleRow}
                    </Box>
                )}

                {description && (
                    <Box sx={{ mb: showNotPublishedAlert ? 4 : 6 }}>
                        <Text variant="body1">{description}</Text>
                    </Box>
                )}

                {showNotPublishedAlert && (
                    <Box sx={{ mb: 2 }}>
                        <Alert
                            action={
                                isManager ? (
                                    <Button
                                        color="inherit"
                                        component={Link}
                                        size="small"
                                        sx={{ fontWeight: 'bold', mr: 1 }}
                                        to="ORGANISATION_SETTINGS"
                                    >
                                        {translate('settings')}
                                    </Button>
                                ) : undefined
                            }
                            severity="error"
                        >
                            {translate(
                                isManager
                                    ? 'libraryPage.notPublishedMessage'
                                    : 'libraryPage.notPublishedAuthorMessage'
                            )}
                        </Alert>
                    </Box>
                )}

                {root && (
                    <Box sx={{ mb: 6 }}>
                        <SearchBar
                            initialValue={searchQuery}
                            placeholder={translate('filterBarPlaceholder')}
                            onClickSearch={onSearch}
                            onEnterPress={onSearch}
                        />
                    </Box>
                )}

                {/* Only show collection list when we have collections or when user can create collections */}
                {(!!collections?.length || canCreateCollection) && (
                    <Box sx={{ mb: 4 }}>
                        {!!collections?.length ? (
                            <LibraryItemsList
                                canSort={canUpdateCollection}
                                collectionId={collectionId}
                                droppableType="COLLECTION"
                                items={collections}
                            />
                        ) : (
                            <List>
                                <ListItem>
                                    <Typography color="text.secondary">
                                        {translate('containsNoCollections')}
                                    </Typography>
                                </ListItem>
                            </List>
                        )}
                    </Box>
                )}

                {/* Only show articles list when we have articles or when user can create articles */}
                {(!!articles?.length || canCreateArticle) && (
                    <Box>
                        {!!articles?.length ? (
                            <LibraryItemsList
                                canSort={canUpdateCollection}
                                collectionId={collectionId}
                                droppableType="ARTICLE"
                                items={articles}
                            />
                        ) : (
                            <List>
                                <ListItem>
                                    <Typography color="text.secondary">
                                        {translate('containsNoArticles')}
                                    </Typography>
                                </ListItem>
                            </List>
                        )}
                    </Box>
                )}

                {canUpdateCollection && collectionId && (
                    <LibraryCollectionDrawer
                        id={collectionId}
                        open={collectionDrawerOpen}
                        pageTitle={pageTitle}
                        onClose={backToCurrentCollection}
                        onDelete={backToParentCollection}
                        onUpdate={backToCurrentCollection}
                    />
                )}

                {canCreateCollection && collectionId && (
                    <LibraryCollectionCreateDrawer
                        open={createCollectionDrawerOpen}
                        pageTitle={pageTitle}
                        parentId={collectionId}
                        onClose={backToCurrentCollection}
                        onCreate={backToCurrentCollection}
                    />
                )}

                {canCreateArticle && collectionId && (
                    <LibraryArticleCreateDrawer
                        open={createArticleDrawerOpen}
                        pageTitle={pageTitle}
                        parentId={collectionId}
                        onClose={backToCurrentCollection}
                        onCreate={(articleId) => {
                            goToArticleContentEditPage(navigate, articleId);
                        }}
                    />
                )}
            </Box>
        </BasePage>
    );
};
